import {
  clearDependentActions,
  EXTRA_PERMISSIONS,
  PERMISSION_ACTIONS,
  PERMISSION_AREAS,
  type GrantablePermission,
  type Permission,
  type PermissionAction,
  type PermissionArea,
} from '@palaccount/contracts';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/components/ui/checkbox';

/** `<area>.<action>` from the closed vocabulary — typed, never templated ad hoc. */
export function permissionOf(area: PermissionArea, action: PermissionAction): Permission {
  return `${area}.${action}`;
}

/**
 * Pure toggle used by the page and pinned by unit tests.
 *
 * BR-PERM-01 rides here: switching an area's View OFF switches Create, Edit,
 * Delete and Approve off with it ("when View is off, the other four must be
 * switched off"), so the grid the person is looking at is never a grid the
 * server would refuse. The server refuses it anyway — this is the courtesy.
 */
export function toggleGrid(
  grid: readonly GrantablePermission[],
  permission: GrantablePermission,
): GrantablePermission[] {
  if (!grid.includes(permission)) return [...grid, permission];

  const next = grid.filter((entry) => entry !== permission);
  const [area, action] = permission.split('.') as [PermissionArea, string];
  return action === 'view' ? clearDependentActions(next, area) : next;
}

/** BR-ADM-06-03's dirty check: same set, order ignored. */
export function sameGrid(
  a: readonly GrantablePermission[],
  b: readonly GrantablePermission[],
): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((entry) => set.has(entry));
}

export interface RoleGridProps {
  /** The grid being shown — the WORKING copy on a custom role. */
  permissions: readonly GrantablePermission[];
  /** BR-ADM-06-04: a System grid is read-only at FULL contrast — legible, immovable. */
  locked: boolean;
  /**
   * BR-PERM-64/BR-ADM-06-17: switches the signed-in person cannot grant.
   * Drawn greyed WITH a tooltip — visually distinct from `locked`
   * (BR-ADM-06-20: "the first says settled, the second says ask the owner").
   */
  ungrantable: ReadonlySet<GrantablePermission>;
  onToggle: (permission: GrantablePermission) => void;
}

/**
 * The 12×5 permission matrix (PART 16.2.2 × 16.3): area rows down, the five
 * actions across, exactly as ADM-06 §5 draws it — twelve rows, never hidden,
 * scrolling if it must (BR-PERM-38).
 */
export function RoleGrid({ permissions, locked, ungrantable, onToggle }: RoleGridProps) {
  const { t } = useTranslation();
  const held = new Set(permissions);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-start">
            <th className="py-2 pe-4 text-start font-medium text-muted-foreground">
              {t('roles.gridArea')}
            </th>
            {PERMISSION_ACTIONS.map((action) => (
              <th key={action} className="px-2 py-2 text-center font-medium text-muted-foreground">
                {t(`roles.action.${action}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PERMISSION_AREAS.map((area) => (
            <tr key={area} className="border-b last:border-0">
              <td className="py-2.5 pe-4 font-medium">{t(`roles.area.${area}`)}</td>
              {PERMISSION_ACTIONS.map((action) => {
                const permission = permissionOf(area, action);
                const cannotGrant = !locked && ungrantable.has(permission) && !held.has(permission);
                // BR-PERM-01: with View off, the other four are switched off
                // and greyed. A LOCKED grid is exempt — 16.4.2's shipped grids
                // all satisfy the rule, so no shipped switch ever reaches this,
                // and a locked grid is drawn at full contrast by BR-ADM-06-04.
                const needsView =
                  !locked && action !== 'view' && !held.has(permissionOf(area, 'view'));
                return (
                  <td key={action} className="px-2 py-1.5 text-center">
                    <Checkbox
                      aria-label={`${t(`roles.area.${area}`)} — ${t(`roles.action.${action}`)}`}
                      checked={held.has(permission)}
                      disabled={locked || cannotGrant || needsView}
                      // Locked grids keep FULL contrast (auditable); only an
                      // ungrantable switch dims (BR-ADM-06-20).
                      className={cannotGrant || needsView ? 'opacity-40' : locked ? 'opacity-100' : ''}
                      title={
                        cannotGrant
                          ? t('roles.cannotGrant')
                          : needsView
                            ? t('roles.needsView', { area: t(`roles.area.${area}`) })
                            : undefined
                      }
                      onCheckedChange={() => {
                        if (!locked && !cannotGrant && !needsView) onToggle(permission);
                      }}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
          {/* The named extensions to the 12×5 grid: the bounced-cheque flag
              override (owner ruling R10 / BR-FIN-10-16) and reopening a
              closed accounting period (GEN-94 + EC-19-21). Both are drawn on
              the Roles screen like every permission (BR-PERM-37) — an access
              review has to be able to answer "who may reopen a reported
              month?" — and no shipped role carries either, so the Owner
              grants them deliberately. */}
          {EXTRA_PERMISSIONS.map((permission) => {
            const cannotGrant = !locked && ungrantable.has(permission) && !held.has(permission);
            return (
              <tr key={permission} className="border-b bg-muted/30 last:border-0">
                <td className="py-2.5 pe-4 font-medium" colSpan={PERMISSION_ACTIONS.length}>
                  {t(`roles.extra.${permission}`)}
                </td>
                <td className="px-2 py-1.5 text-center">
                  <Checkbox
                    aria-label={t(`roles.extra.${permission}`)}
                    checked={held.has(permission)}
                    disabled={locked || cannotGrant}
                    className={cannotGrant ? 'opacity-40' : locked ? 'opacity-100' : ''}
                    title={cannotGrant ? t('roles.cannotGrant') : undefined}
                    onCheckedChange={() => {
                      if (!locked && !cannotGrant) onToggle(permission);
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
